import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AttemptAccessRouteProps {
  children: React.ReactNode;
}

/**
 * Guard for the exam runner and its result/review pages.
 *
 * An authenticated user is treated exactly like ProtectedRoute (no subscription
 * gating here). A logged-out visitor is allowed through only when the URL
 * carries an `attemptId`; that attempt is a free-trial attempt the visitor just
 * started. The backend still enforces ownership and the trial/subscription
 * gate, so a guessed or paid attempt id is refused server-side.
 *
 * This exists instead of relaxing ProtectedRoute so that account-only and
 * subscription-gated pages keep their original guard untouched.
 */
export const AttemptAccessRoute: React.FC<AttemptAccessRouteProps> = ({
  children,
}) => {
  const { loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const hasAttemptId = !!new URLSearchParams(location.search).get('attemptId');

  useEffect(() => {
    if (loading) return;

    if (!isAuthenticated && !hasAttemptId) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, hasAttemptId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated && !hasAttemptId) {
    return null;
  }

  return <>{children}</>;
};

export default AttemptAccessRoute;
